import { config } from './environment';

// API Endpoint definitions organized by service
export const endpoints = {
  // Main API endpoints
  main: {
    auth: {
      login: '/auth/login',
      loginCoach: '/auth/login/coach',
      loginClient: '/auth/login/client',
      loginAdmin: '/auth/login/admin',
      register: '/auth/register',
      logout: '/auth/logout',
      refresh: '/auth/refresh',
      profile: '/auth/profile',
    },
    admin: {
      coaches: '/admin/coaches',
      coach: (id: string) => `/admin/coaches/${id}`,
      coachBilling: (id: string) => `/admin/coaches/${id}/billing`,
      clients: '/admin/clients',
      client: (id: string) => `/admin/clients/${id}`,
      stats: '/admin/stats',
      emailSettings: '/admin/email/settings',
      emailTest: '/admin/email/test',
      specializations: '/admin/specializations',
      specialization: (id: number) => `/admin/specializations/${id}`,
      googleAuthUrl: '/admin/google/auth-url',
      googleStatus: '/admin/google/status',
      googleDisconnect: '/admin/google/disconnect',
      ragStatus: '/admin/rag/status',
      ragIngest: '/admin/rag/ingest',
      ragClearFramework: '/admin/rag/framework',
      ragReembedHistory: '/admin/rag/reembed-history',
      testSummaryByCoach: (coachId: string) => `/admin/test-summary/coach/${coachId}`,
      testSummaryDetail: (transcriptId: string) => `/admin/test-summary/${transcriptId}`,
      costAudit: '/admin/cost-audit',
      costAuditSummary: '/admin/cost-audit/summary',
      servicePricing: '/admin/service-pricing',
      servicePricingItem: (id: string) => `/admin/service-pricing/${id}`,
      auditLogs: '/audit/logs',
      coachClientAppointments: (coachId: string, clientId: string) => `/admin/coach/${coachId}/client/${clientId}/appointments`,
      appointmentNotes: (appointmentId: string) => `/admin/appointments/${appointmentId}/notes`,
      appointmentTranscript: (appointmentId: string) => `/admin/appointments/${appointmentId}/summarize-transcript`,
      countries: '/admin/countries',
      country: (id: string) => `/admin/countries/${id}`,
      certifications: '/admin/certifications',
      certification: (id: number) => `/admin/certifications/${id}`,
      institutes: '/admin/institutes',
      institute: (id: number) => `/admin/institutes/${id}`,
      cronJobs: '/admin/cron-jobs',
      cronJob: (jobName: string) => `/admin/cron-jobs/${jobName}`,
      cronJobTrigger: (jobName: string) => `/admin/cron-jobs/${jobName}/trigger`,
      cronJobHistory: (jobName: string) => `/admin/cron-jobs/${jobName}/history`,
      billingSessions: (coachId: string, month?: string) =>
        month ? `/admin/billing/sessions?coach_id=${coachId}&month=${month}` : `/admin/billing/sessions?coach_id=${coachId}`,
      runBillingForCoach: (coachId: string, month: string, forceRegenerate?: boolean, processPayment?: boolean) =>
        `/admin/billing/run-for-coach?coach_id=${coachId}&month=${month}${forceRegenerate ? '&force_regenerate=true' : ''}${processPayment ? '&process_payment=true' : ''}`,
      billingInvoices: (coachId: string) => `/admin/billing/invoices?coach_id=${coachId}`,
      billingInvoicePdf: (invoiceId: string) => `/admin/billing/invoices/${invoiceId}/pdf`,
      billingEventLog: (coachId: string) => `/admin/billing/event-log?coach_id=${coachId}`,
    },
    demo: {
      configured: '/demo/configured',
      slots: '/demo/slots',
      book: '/demo/book',
    },
    specializations: '/specializations',
    users: {
      list: '/users',
      get: (id: string) => `/users/${id}`,
      update: (id: string) => `/users/${id}`,
      delete: (id: string) => `/users/${id}`,
    },
    clients: {
      list: '/clients',
      get: (id: string) => `/clients/${id}`,
      create: '/clients',
      register: '/clients/register',
      update: (id: string) => `/clients/${id}`,
      delete: (id: string) => `/clients/${id}`,
      search: '/clients/search',
      sessions: (id: string | number) => `/clients/${id}/sessions`,
      dashboard: (id: string | number) => `/clients/${id}/dashboard`,
      me: '/clients/me',
      deliveryPref: '/clients/me/delivery-preference',
    },
    coaches: {
      list: '/coaches',
      get: (id: string) => `/coaches/${id}`,
      create: '/coaches',
      register: '/coaches/register',
      update: (id: string) => `/coaches/${id}`,
      delete: (id: string) => `/coaches/${id}`,
      search: '/coaches/search',
      sessions: (id: string) => `/coaches/${id}/sessions`,
      createSession: '/coaches/create-session',
      preferences: (id: string | number) => `/coaches/${id}/preferences`,
      appointments: (id: string) => `/coaches/${id}/sessions`,
      me: '/coaches/me',
      promptSettings: '/coaches/me/prompt-settings',
      promptSettingsDefaults: '/coaches/me/prompt-settings/defaults',
      promptSettingsHistory: '/coaches/me/prompt-settings/history',
      promptSettingsRevert: (version: number) => `/coaches/me/prompt-settings/revert/${version}`,
      promptSettingsPreview: '/coaches/me/prompt-settings/preview',
      onboardingStatus: '/coaches/me/onboarding-status',
      workingHours: '/coaches/me/working-hours',
      logo: '/coaches/me/logo',
      sessionsLog: '/coaches/me/sessions-log',
      manualSessions: '/coaches/me/sessions-log/manual',
      updateManualSession: (id: string) => `/coaches/sessions/log/${id}`,
      deleteManualSession: (id: string) => `/coaches/sessions/log/${id}`,
      clientAiConfig: (coachClientId: number) => `/coaches/me/clients/${coachClientId}/ai-config`,
      clientNotifPrefs: (clientId: string) => `/coaches/me/clients/${clientId}/notification-preferences`,
      deleteClient: (coachId: string, clientId: string) => `/coaches/${coachId}/clients/${clientId}`,
      exportClientData: (coachId: string, clientId: string) => `/coaches/${coachId}/clients/${clientId}/export`,
      clientOutstandingPayments: (clientId: string) => `/coaches/me/outstanding-payments?client_id=${clientId}`,
      clientRevenueSummary: (clientId: string) => `/coaches/me/clients/${clientId}/revenue-summary`,
      markSessionPaid: (appointmentId: string) => `/coaches/me/sessions/${appointmentId}/mark-paid`,
      updateSessionType: (appointmentId: string) => `/coaches/sessions/${appointmentId}/type`,
    },
    clientNotes: {
      list:   (coachClientId: string) => `/coach-clients/${coachClientId}/notes`,
      create: (coachClientId: string) => `/coach-clients/${coachClientId}/notes`,
      update: (coachClientId: string, noteId: string) => `/coach-clients/${coachClientId}/notes/${noteId}`,
      delete: (coachClientId: string, noteId: string) => `/coach-clients/${coachClientId}/notes/${noteId}`,
      search: (coachClientId: string, q: string) => `/coach-clients/${coachClientId}/search?q=${encodeURIComponent(q)}`,
    },
    session: {
      info:    (appointmentId: string) => `/session/${appointmentId}/info`,
      summary: (sessionId: string) => `/session/${sessionId}/summary`,
      updateSummary: (sessionId: string) => `/session/${sessionId}/summary`,
      summarize: (sessionId: string) => `/session/${sessionId}/summarize-transcript`,
      evaluate: (sessionId: string) => `/session/${sessionId}/evaluation`,
      rate: (sessionId: string) => `/session/${sessionId}/summary/rate`,
      approveSummary: (sessionId: string) => `/session/${sessionId}/summary/approve`,
      sendNotesToClient: (sessionId: string) => `/session/${sessionId}/summary/send-to-client`,
      reprocess: (sessionId: string) => `/session/${sessionId}/reprocess`,
      approveAllActionItems: (sessionId: string) => `/session/${sessionId}/action-items/approve-all`,
    },
    actionItems: {
      byClient: (clientId: string) => `/action-items/client/${clientId}`,
      byClientCoach: (clientId: string) => `/action-items/coach/client/${clientId}`,
      bySession: (sessionId: string) => `/action-items/session/${sessionId}`,
      coachMyTasks: '/action-items/coach/my-tasks',
      create: '/action-items',
      update: (id: string) => `/action-items/${id}`,
      delete: (id: string) => `/action-items/${id}`,
      approve: (id: string) => `/action-items/${id}/approve`,
      sendToClient: (sessionId: string) => `/action-items/session/${sessionId}/send-to-client`,
      review: (token: string) => `/action-items/review/${token}`,
      reviewUpdate: (token: string, itemId: string) => `/action-items/review/${token}/${itemId}`,
    },
    nudges: {
      send: (itemId: string) => `/nudges/send/${itemId}`,
    },
    nudgeSchedules: {
      list: '/nudge-schedules/',
      create: '/nudge-schedules/',
      get: (id: string) => `/nudge-schedules/${id}`,
      update: (id: string) => `/nudge-schedules/${id}`,
      delete: (id: string) => `/nudge-schedules/${id}`,
      sendNow: (id: string) => `/nudge-schedules/${id}/send-now`,
      history: '/nudge-schedules/history',
      myReminders: '/nudge-schedules/my-reminders',
    },
    questionnaires: {
      generate: '/questionnaires/generate',
      create: '/questionnaires',
      byClient: (clientId: string) => `/questionnaires/by-client/${clientId}`,
      pending: (clientId: string) => `/questionnaires/by-client/${clientId}/pending`,
      assigned: (clientId: string) => `/questionnaires/by-client/${clientId}/assigned`,
      detail: (id: string) => `/questionnaires/${id}`,
      update: (id: string) => `/questionnaires/${id}`,
      delete: (id: string) => `/questionnaires/${id}`,
      respond: (id: string) => `/questionnaires/${id}/respond`,
      responses: (id: string) => `/questionnaires/${id}/responses`,
      myResponses: (id: string) => `/questionnaires/${id}/my-responses`,
      completedByClient: (clientId: string) => `/questionnaires/by-client/${clientId}/completed`,
      sendReminder: (id: string) => `/questionnaires/${id}/send-reminder`,
    },
    documents: {
      upload: '/documents/upload',
      list: '/documents',
      delete: (id: string) => `/documents/${id}`,
      packets: '/documents/packets',
      packetById: (id: string) => `/documents/packets/${id}`,
      packetsForClient: '/documents/packets/for-client',
      packetByToken: (token: string) => `/documents/packets/by-token/${token}`,
      signPacket: (token: string) => `/documents/packets/by-token/${token}/sign`,
      viewFile: (token: string, docId: string) => `/documents/view/${token}/${docId}`,
      documentTypes: '/documents/types',
      documentType: (id: string) => `/documents/types/${id}`,
      documentCopy: (id: string) => `/documents/${id}/copy`,
      documentViewUrl: (id: string) => `/documents/${id}/view-url`,
      clientFolders: '/documents/client-folders',
      clientUpload: '/documents/client-upload',
      clientFiles: '/documents/client-files',
      clientFile: (id: string) => `/documents/client-files/${id}`,
    },
    scheduling: {
      eventTypes: (coachId: string) => `/scheduling/${coachId}/event-types`,
      slots: (coachId: string) => `/scheduling/${coachId}/slots`,
      book: (coachId: string) => `/scheduling/${coachId}/book`,
    },
    packages: {
      list: '/packages',
      create: '/packages',
      package: (id: string) => `/packages/${id}`,
      enroll: (id: string) => `/packages/${id}/enroll`,
      packageEnrollments: (id: string) => `/packages/${id}/enrollments`,
      allEnrollments: '/packages/enrollments',
      enrollment: (id: string) => `/packages/enrollments/${id}`,
      clientActive: '/packages/me/package-enrollments',
      clientHistory: '/packages/me/package-enrollments/history',
      clientPendingPayments: '/packages/me/pending-payments',
    },
    recurringSeries: {
      list: '/recurring-series',
      create: '/recurring-series',
      get: (id: string) => `/recurring-series/${id}`,
      cancel: (id: string) => `/recurring-series/${id}/cancel`,
    },
    sessionPrep: {
      get: (appointmentId: string) => `/session-prep/${appointmentId}`,
      generate: (appointmentId: string) => `/session-prep/${appointmentId}/generate`,
      updateAnnotations: (appointmentId: string) => `/session-prep/${appointmentId}`,
    },
    calendly: {
      authUrl: '/calendly/auth-url',
      status: '/calendly/status',
      disconnect: '/calendly/disconnect',
      eventTypes: '/calendly/event-types',
      settings: '/calendly/settings',
      bookingInfo: (coachId: string) => `/calendly/booking/${coachId}`,
    },
    zoom: {
      authUrl: '/zoom/auth-url',
      status: '/zoom/status',
      disconnect: '/zoom/disconnect',
    },
    eventTypes: {
      list: '/event-types',
      create: '/event-types',
      update: (id: string) => `/event-types/${id}`,
      delete: (id: string) => `/event-types/${id}`,
      schedulingConfig: '/event-types/scheduling-config/me',
    },
    booking: {
      info: (coachId: string) => `/book/${coachId}`,
      slots: (coachId: string) => `/book/${coachId}/slots`,
      book: (coachId: string) => `/book/${coachId}`,
    },
    help: {
      ask: '/help/ask',
      docs: '/help/docs',
      doc: (category: string, slug: string) => `/help/docs/${category}/${slug}`,
    },
    billing: {
      summary: '/billing/summary',
      sessions: (month?: string) => month ? `/billing/sessions?month=${month}` : '/billing/sessions',
      invoices: '/billing/invoices',
      applyCoupon: '/billing/coupon/apply',
      removeCoupon: '/billing/coupon',
      subscribe: '/billing/subscribe',
      upgrade: '/billing/upgrade',
      downgrade: '/billing/downgrade',
      gateStatus: '/billing/gate-status',
      startTrial: '/billing/start-trial',
      trialStatus: '/billing/trial-status',
      setupIntent: '/billing/setup-intent',
      setDefaultPaymentMethod: '/billing/set-default-payment-method',
      validateCoupon: '/billing/validate-coupon',
      paymentMethod: '/billing/payment-method',
      portalSession: '/billing/portal-session',
      cancel: '/billing/cancel',
      reactivate: '/billing/reactivate',
    },
    social: {
      // OAuth
      authUrl: (platform: string) => `/social/${platform}/auth-url`,
      callback: (platform: string) => `/social/${platform}/callback`,
      status: (platform: string) => `/social/${platform}/status`,
      disconnect: (platform: string) => `/social/${platform}/disconnect`,
      // Posts
      posts: '/social/posts',
      post: (id: string) => `/social/posts/${id}`,
      publishPost: (id: string) => `/social/posts/${id}/publish`,
      // Image
      uploadImage: '/social/upload-image',
      deleteImage: (key: string) => `/social/upload-image/${encodeURIComponent(key)}`,
      // AI draft
      aiDraft: '/social/ai-draft',
      // Cron
      processScheduled: '/social/process-scheduled',
    },
  },

  // Assessment service endpoints
  assessment: {
    sessions: {
      list: '/assessment/sessions',
      get: (id: string) => `/assessment/sessions/${id}`,
      create: '/assessment/sessions',
      update: (id: string) => `/assessment/sessions/${id}`,
      delete: (id: string) => `/assessment/sessions/${id}`,
      start: (id: string) => `/assessment/sessions/${id}/start`,
      complete: (id: string) => `/assessment/sessions/${id}/complete`,
    },
    types: {
      list: '/assessment/types',
      get: (id: string) => `/assessment/types/${id}`,
      create: '/assessment/types',
      update: (id: string) => `/assessment/types/${id}`,
    },
    questions: {
      list: '/assessment/questions',
      get: (id: string) => `/assessment/questions/${id}`,
      generate: '/assessment/questions/generate',
      adaptive: '/assessment/questions/adaptive',
    },
    responses: {
      submit: '/assessment/responses',
      get: (sessionId: string) => `/assessment/responses/${sessionId}`,
      update: (id: string) => `/assessment/responses/${id}`,
    },
    insights: {
      get: (sessionId: string) => `/assessment/insights/${sessionId}`,
      generate: (sessionId: string) => `/assessment/insights/${sessionId}/generate`,
      history: (clientId: string) => `/assessment/insights/history/${clientId}`,
    },
  },

  // Meeting intelligence endpoints
  meeting: {
    sessions: {
      list: '/meeting/sessions',
      get: (id: string) => `/meeting/sessions/${id}`,
      create: '/meeting/sessions',
      update: (id: string) => `/meeting/sessions/${id}`,
      delete: (id: string) => `/meeting/sessions/${id}`,
      start: (id: string) => `/meeting/sessions/${id}/start`,
      end: (id: string) => `/meeting/sessions/${id}/end`,
    },
    recording: {
      start: (sessionId: string) => `/meeting/recording/${sessionId}/start`,
      stop: (sessionId: string) => `/meeting/recording/${sessionId}/stop`,
      upload: (sessionId: string) => `/meeting/recording/${sessionId}/upload`,
    },
    transcription: {
      get: (sessionId: string) => `/meeting/transcription/${sessionId}`,
      process: (sessionId: string) => `/meeting/transcription/${sessionId}/process`,
      realtime: (sessionId: string) => `/meeting/transcription/${sessionId}/realtime`,
    },
    analysis: {
      get: (sessionId: string) => `/meeting/analysis/${sessionId}`,
      generate: (sessionId: string) => `/meeting/analysis/${sessionId}/generate`,
      actionItems: (sessionId: string) => `/meeting/analysis/${sessionId}/action-items`,
      summary: (sessionId: string) => `/meeting/analysis/${sessionId}/summary`,
    },
    scheduling: {
      availability: '/meeting/scheduling/availability',
      book: '/meeting/scheduling/book',
      cancel: (id: string) => `/meeting/scheduling/${id}/cancel`,
      reschedule: (id: string) => `/meeting/scheduling/${id}/reschedule`,
    },
  },

  // AI service endpoints
  ai: {
    completion: '/ai/completion',
    assessment: '/ai/assessment',
    meeting: '/ai/meeting',
    transcription: '/ai/transcription',
    videoAnalysis: '/ai/video-analysis',
    adaptiveQuestions: '/ai/adaptive-questions',
    progressAnalysis: '/ai/progress-analysis',
    recommendations: '/ai/recommendations',
    health: '/ai/health',
  },

  // Progress tracking endpoints
  progress: {
    goals: {
      list: (clientId: string) => `/progress/goals/${clientId}`,
      get: (id: string) => `/progress/goals/${id}`,
      create: '/progress/goals',
      update: (id: string) => `/progress/goals/${id}`,
      delete: (id: string) => `/progress/goals/${id}`,
    },
    milestones: {
      list: (goalId: string) => `/progress/milestones/${goalId}`,
      get: (id: string) => `/progress/milestones/${id}`,
      create: '/progress/milestones',
      update: (id: string) => `/progress/milestones/${id}`,
      complete: (id: string) => `/progress/milestones/${id}/complete`,
    },
    tracking: {
      get: (clientId: string) => `/progress/tracking/${clientId}`,
      update: (clientId: string) => `/progress/tracking/${clientId}`,
      history: (clientId: string) => `/progress/tracking/${clientId}/history`,
      trends: (clientId: string) => `/progress/tracking/${clientId}/trends`,
    },
  },

  // Resource management endpoints
  resources: {
    list: '/resources',
    get: (id: string) => `/resources/${id}`,
    create: '/resources',
    update: (id: string) => `/resources/${id}`,
    delete: (id: string) => `/resources/${id}`,
    search: '/resources/search',
    categories: '/resources/categories',
    recommendations: (clientId: string) => `/resources/recommendations/${clientId}`,
  },
};

// Helper function to build full URLs
export const buildUrl = (baseUrl: string, endpoint: string): string => {
  return `${baseUrl}${endpoint}`;
};

// Environment-specific URL builders
export const urlBuilders = {
  main: (endpoint: string) => buildUrl(config.apiBaseUrl, endpoint),
};

// Export default endpoints
export default endpoints;
